import { useCallback } from 'react';
import { useLocalStorageState } from '@reflex/ui/hooks/useWebStorageState';

/**
 * How the agent sidebar orders its sessions. Purely a client-side presentation
 * preference: every order lists the same sessions, it only changes their
 * position.
 *
 * - `created`: newest-created sessions first (the default).
 * - `activity`: most recently active sessions first (last stream event, user
 *   message, or lifecycle change), falling back to creation time for legacy
 *   runs that never recorded activity.
 */
export const SESSION_SORTS = ['activity', 'created'] as const;
export type SessionSort = (typeof SESSION_SORTS)[number];

export const DEFAULT_SESSION_SORT: SessionSort = 'created';

const STORAGE_KEY = 'agent-session-sort';

export interface SessionSortOption {
  value: SessionSort;
  label: string;
  description: string;
}

/** Display metadata for the settings picker, in presentation order. */
export const SESSION_SORT_OPTIONS: readonly SessionSortOption[] = [
  {
    value: 'activity',
    label: 'Recent activity',
    description: 'Most recently active sessions first.',
  },
  {
    value: 'created',
    label: 'Date created',
    description: 'Newest sessions first.',
  },
];

/**
 * Parse a persisted value back into a `SessionSort`. localStorage is an
 * untrusted boundary (stale keys from older builds, manual edits), so an
 * unknown value falls back to the default instead of leaking through.
 */
export function parseSessionSort(value: unknown): SessionSort {
  return SESSION_SORTS.includes(value as SessionSort)
    ? (value as SessionSort)
    : DEFAULT_SESSION_SORT;
}

/**
 * The user's agent-session sort order, persisted per browser (same mechanism
 * as the nav style and sidebar width). Reactive across components and tabs via
 * `useLocalStorageState`, so the sidebar reorders immediately when the setting
 * changes on the settings page.
 */
export function useSessionSort(): [SessionSort, (sort: SessionSort) => void] {
  const [raw, setRaw] = useLocalStorageState<string>(STORAGE_KEY, DEFAULT_SESSION_SORT);
  const setSort = useCallback((sort: SessionSort) => setRaw(sort), [setRaw]);
  return [parseSessionSort(raw), setSort];
}
